/* One widget on the home page. The shell owns everything a widget shouldn't hand-roll: collapse, the ⋯ menu,
   density, row selection and keys, and the action bar (href + actions). Keys act only while this widget is the
   active one: j/k ↓/↑ move · Enter details · o open · action letters · d density · c collapse · Esc clear. */
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@aix/ui"
import { Link, useNavigate } from "@tanstack/react-router"
import { ChevronRight, MoreHorizontal, RefreshCw } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import type { MouseEvent as ReactMouseEvent } from "react"

import { EDensity, EVerdict } from "../homeDashboard.types"
import type {
  ICompanyEntry,
  IRowAction,
  IWidgetDefinition,
  IWidgetResult,
  IWidgetRow,
  IWidgetSummary,
} from "../homeDashboard.types"
import {
  DENSITY_OPTIONS,
  EMove,
  SNOOZE_OPTIONS,
  ago,
  hasModifier,
  isTyping,
  nextDensity,
  rowInCompanies,
  rowMatches,
  splitRows,
  stepIndex,
  summarize,
} from "../homeDashboardModel"

export interface IWidgetSectionProps {
  def: IWidgetDefinition
  collapsed: boolean
  filter: string
  flash: boolean
  /** The widget keys act on (1–9, h/l, or a click inside it) */
  active: boolean
  density: EDensity
  /** Home company filter; rows outside it are dropped before sorting, counts and keys */
  companyFilter: { selected: string[]; directory: ICompanyEntry[] }
  canMove: Record<EMove, boolean>
  now: number
  onActivate: (id: string) => void
  onToggleCollapse: (id: string) => void
  onDensity: (id: string, density: EDensity) => void
  onSnooze: (id: string, days: number) => void
  onHide: (id: string) => void
  onMove: (id: string, move: EMove) => void
  onSummary: (id: string, summary: IWidgetSummary) => void
}

const MOVE_LABELS: [EMove, string][] = [
  [EMove.UP, "Move up"],
  [EMove.DOWN, "Move down"],
  [EMove.LEFT, "Move to left column"],
  [EMove.RIGHT, "Move to right column"],
]

/** The "N more" quiet line is selectable too; Enter folds it open */
const QUIET_KEY = "__quiet"
const FIELD_SELECTOR = "input, textarea, select"

const isExternal = (href: string) => !href.startsWith("/")

function Pill({ text, verdict }: { text: string; verdict: EVerdict }) {
  return <span className={`hd-pill hd-${verdict}`}>{text}</span>
}

function RowActions({
  row,
  onOpen,
  onAction,
}: {
  row: IWidgetRow
  onOpen: () => void
  onAction: (action: IRowAction) => void
}) {
  if (!row.href && !row.actions?.length) return null
  const label = row.hrefLabel ?? "Open"
  return (
    <div className="hd-actions">
      {row.href &&
        (isExternal(row.href) ? (
          <a href={row.href} target="_blank" rel="noopener noreferrer">
            <kbd>o</kbd>
            {label} ↗
          </a>
        ) : (
          <a
            href={row.href}
            onClick={(e: ReactMouseEvent) => {
              e.preventDefault()
              onOpen()
            }}
          >
            <kbd>o</kbd>
            {label}
          </a>
        ))}
      {row.actions?.map((action) => (
        <button key={action.key} type="button" disabled={action.disabled} onClick={() => onAction(action)}>
          <kbd>{action.key}</kbd>
          {action.label}
        </button>
      ))}
    </div>
  )
}

interface IRowProps {
  row: IWidgetRow
  open: boolean
  selected: boolean
  onToggle: () => void
  onOpen: () => void
  onAction: (action: IRowAction) => void
}

function Row({ row, open, selected, onToggle, onOpen, onAction }: IRowProps) {
  return (
    <li className={`hd-row${open ? " open" : ""}${selected ? " selected" : ""}`} data-row-key={row.key}>
      <button type="button" className="hd-row-head" aria-expanded={open} aria-current={selected} onClick={onToggle}>
        <Pill text={row.pill?.text ?? row.verdict} verdict={row.pill?.verdict ?? row.verdict} />
        <span className="hd-row-main">
          <span className="hd-row-title">{row.title}</span>
          {row.sub && <span className="hd-row-sub">{row.sub}</span>}
        </span>
        {row.meta && <span className="hd-row-meta">{row.meta}</span>}
        <ChevronRight className="hd-chev" aria-hidden="true" />
      </button>
      {open && (
        <div className="hd-details">
          {row.details}
          <RowActions row={row} onOpen={onOpen} onAction={onAction} />
          {!row.details && !row.href && !row.actions?.length && <p className="hd-muted">No details.</p>}
        </div>
      )}
    </li>
  )
}

/** Key hints for the selected row, shown in the active widget's footer. */
function Hints({ row }: { row: IWidgetRow | undefined }) {
  const linkOnly = !!row?.href && !row.details && !row.actions?.length
  return (
    <span className="hd-hints">
      {!linkOnly && (
        <span>
          <kbd>↵</kbd> {row ? "details" : "expand"}
        </span>
      )}
      {row?.href && (
        <span>
          <kbd>{linkOnly ? "↵ o" : "o"}</kbd> {row.hrefLabel?.toLowerCase() ?? "open"}
        </span>
      )}
      {row?.actions
        ?.filter((action) => !action.disabled)
        .map((action) => (
          <span key={action.key}>
            <kbd>{action.key}</kbd> {action.label.toLowerCase()}
          </span>
        ))}
    </span>
  )
}

function SectionMenu({
  def,
  canMove,
  density,
  onDensity,
  onSnooze,
  onHide,
  onMove,
}: Pick<IWidgetSectionProps, "def" | "canMove" | "density" | "onDensity" | "onSnooze" | "onHide" | "onMove">) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className="hd-icon-btn" aria-label={`${def.title} options`}>
          <MoreHorizontal className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>{def.title}</DropdownMenuLabel>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>Density</DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuRadioGroup value={density} onValueChange={(v) => onDensity(def.id, v as EDensity)}>
              {DENSITY_OPTIONS.map((option) => (
                <DropdownMenuRadioItem key={option.value} value={option.value}>
                  {option.label}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>Snooze</DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            {SNOOZE_OPTIONS.map((option) => (
              <DropdownMenuItem key={option.days} onSelect={() => onSnooze(def.id, option.days)}>
                {option.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSeparator />
        {MOVE_LABELS.map(([move, label]) => (
          <DropdownMenuItem key={move} disabled={!canMove[move]} onSelect={() => onMove(def.id, move)}>
            {label}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => onHide(def.id)}>Remove from home</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export function WidgetSection(props: IWidgetSectionProps) {
  const { def, collapsed, filter, flash, active, density, companyFilter, now } = props
  const { onActivate, onToggleCollapse, onDensity, onSummary } = props
  const navigate = useNavigate()
  const state = def.useWidget()
  const [openRows, setOpenRows] = useState<Set<string>>(() => new Set())
  const [quietOpen, setQuietOpen] = useState(false)
  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  /** Row whose first field should take focus once its details render (expand actions) */
  const [focusKey, setFocusKey] = useState<string | null>(null)
  const sectionRef = useRef<HTMLElement>(null)
  const { selected: companies, directory } = companyFilter
  const raw = state.result
  const filtering = companies.length > 0
  // A widget that's about a selected company keeps its lead (Mission's goal); otherwise the lead is all-company totals
  const scopedWidget = !!def.companies?.some((slug) => companies.includes(slug))
  const inScope = raw && filtering ? raw.rows.filter((row) => rowInCompanies(row, def, companies, directory)) : null
  const result: IWidgetResult | undefined =
    raw && inScope && inScope.length < raw.rows.length
      ? {
          ...raw,
          rows: inScope,
          lead: scopedWidget ? raw.lead : undefined,
          count: `${inScope.length} of ${raw.rows.length}`,
        }
      : raw
  const summary = summarize(result, !!state.error)

  useEffect(() => {
    onSummary(def.id, summary)
    // Report only when the numbers change, not on every render
  }, [def.id, onSummary, summary.verdict, summary.act, summary.watch, summary.error])

  const { loud, quiet } = result ? splitRows(result) : { loud: [], quiet: [] }
  const shownLoud = loud.filter((row) => rowMatches(row, filter))
  const shownQuiet = quiet.filter((row) => rowMatches(row, filter))
  const showQuiet = quietOpen || !!filter

  // Selection walks rows in screen order: loud rows, the quiet line, then quiet rows when unfolded
  const navKeys = collapsed
    ? []
    : [
        ...shownLoud.map((row) => row.key),
        ...(shownQuiet.length ? [QUIET_KEY] : []),
        ...(showQuiet ? shownQuiet.map((row) => row.key) : []),
      ]
  const rowByKey = new Map([...shownLoud, ...shownQuiet].map((row) => [row.key, row]))
  const selectedRow = selectedKey ? rowByKey.get(selectedKey) : undefined
  const selectedIndex = selectedKey ? navKeys.indexOf(selectedKey) : -1

  const setOpen = (key: string, open: boolean) =>
    setOpenRows((prev) => {
      const next = new Set(prev)
      if (open) next.add(key)
      else next.delete(key)
      return next
    })

  const openHref = (row: IWidgetRow) => {
    if (!row.href) return
    if (isExternal(row.href)) window.open(row.href, "_blank", "noopener,noreferrer")
    // `href` (not `to`) so an in-app link's ?query reaches the target route as search params
    else void navigate({ href: row.href })
  }

  const runAction = (row: IWidgetRow, action: IRowAction) => {
    if (action.disabled) return
    if ("run" in action) {
      action.run()
      return
    }
    setOpen(row.key, true)
    setFocusKey(row.key)
  }

  const toggle = (key: string) => {
    if (key === QUIET_KEY) setQuietOpen(!quietOpen)
    else setOpen(key, !openRows.has(key))
  }

  // Keys only reach the active widget; the latest closure is read through a ref so the listener stays put
  const onKey = useRef<(e: KeyboardEvent) => void>(() => undefined)
  onKey.current = (e: KeyboardEvent) => {
    if (isTyping(e.target) || hasModifier(e)) return
    const key = e.key
    if (key === "d") onDensity(def.id, nextDensity(density))
    else if (key === "c") onToggleCollapse(def.id)
    else if (key === "j" || key === "ArrowDown" || key === "k" || key === "ArrowUp") {
      const next = navKeys[stepIndex(navKeys.length, selectedIndex, key === "j" || key === "ArrowDown" ? 1 : -1)]
      if (!next) return
      e.preventDefault()
      setSelectedKey(next)
    } else if (key === "Escape" && selectedKey) setSelectedKey(null)
    else if (!selectedKey || selectedIndex < 0) return
    else if (key === "Enter") {
      e.preventDefault()
      // A row with nothing to expand opens its link instead
      if (selectedRow && !selectedRow.details && !selectedRow.actions?.length && selectedRow.href) openHref(selectedRow)
      else toggle(selectedKey)
    } else if (key === "o" && selectedRow) openHref(selectedRow)
    else {
      const action = selectedRow?.actions?.find((a) => a.key === key)
      if (selectedRow && action) {
        e.preventDefault()
        runAction(selectedRow, action)
      }
    }
  }

  useEffect(() => {
    if (!active) return
    const listener = (e: KeyboardEvent) => onKey.current(e)
    document.addEventListener("keydown", listener)
    return () => document.removeEventListener("keydown", listener)
  }, [active])

  useEffect(() => {
    if (!selectedKey || !active) return
    sectionRef.current
      ?.querySelector(`[data-row-key="${CSS.escape(selectedKey)}"]`)
      ?.scrollIntoView({ block: "nearest" })
  }, [selectedKey, active])

  useEffect(() => {
    if (!focusKey) return
    const details = sectionRef.current?.querySelector(`[data-row-key="${CSS.escape(focusKey)}"] .hd-details`)
    const field = details?.querySelector<HTMLElement>(FIELD_SELECTOR) ?? details?.querySelector<HTMLElement>("button")
    field?.focus()
    setFocusKey(null)
  }, [focusKey, openRows])

  if (result?.hide) return null

  const renderRow = (row: IWidgetRow) => (
    <Row
      key={row.key}
      row={row}
      open={openRows.has(row.key)}
      selected={active && selectedKey === row.key}
      onToggle={() => {
        setSelectedKey(row.key)
        toggle(row.key)
      }}
      onOpen={() => openHref(row)}
      onAction={(action) => runAction(row, action)}
    />
  )

  return (
    <section
      ref={sectionRef}
      id={`hd-${def.id}`}
      className={`hd-section hd-density-${density}${collapsed ? " collapsed" : ""}${flash ? " flash" : ""}${active ? " active" : ""}`}
      aria-label={def.title}
      onPointerDown={() => onActivate(def.id)}
    >
      <header className="hd-section-head">
        <h2>
          <button
            type="button"
            className="hd-sec-toggle"
            aria-expanded={!collapsed}
            onClick={() => onToggleCollapse(def.id)}
          >
            <ChevronRight className="hd-chev" aria-hidden="true" />
            <span className="hd-sec-title">{def.title}</span>
          </button>
        </h2>
        <div className="hd-right">
          {state.isFetching && <RefreshCw className="h-3 w-3 animate-spin hd-muted" aria-label="Refreshing" />}
          {result?.count && <span className="hd-count">{result.count}</span>}
          {state.error ? (
            <Pill text="error" verdict={EVerdict.UNKNOWN} />
          ) : (
            result && <Pill text={result.verdict} verdict={result.verdict} />
          )}
          <SectionMenu {...props} />
        </div>
      </header>
      {!collapsed && (
        <>
          {state.isLoading && <p className="hd-state">Loading…</p>}
          {state.error && !result && (
            <p className="hd-state hd-error">
              {def.title} didn&apos;t load: {state.error}{" "}
              <button type="button" className="hd-quiet-btn" onClick={state.refetch}>
                Retry
              </button>
            </p>
          )}
          {result && (
            <>
              {result.lead && !filter && <div className="hd-lead">{result.lead}</div>}
              <ul className="hd-rows">
                {shownLoud.map(renderRow)}
                {shownQuiet.length > 0 && (
                  <li
                    className={`hd-row hd-quiet${showQuiet ? " open" : ""}${active && selectedKey === QUIET_KEY ? " selected" : ""}`}
                    data-row-key={QUIET_KEY}
                  >
                    <button
                      type="button"
                      className="hd-row-head"
                      aria-expanded={showQuiet}
                      onClick={() => {
                        setSelectedKey(QUIET_KEY)
                        setQuietOpen(!quietOpen)
                      }}
                    >
                      <Pill text="ok" verdict={EVerdict.CLEAR} />
                      <span className="hd-row-main">
                        <span className="hd-row-title">{result.quietLabel || `${quiet.length} more`}</span>
                        <span className="hd-row-sub">{shownQuiet.map((row) => row.title).join(", ")}</span>
                      </span>
                      <ChevronRight className="hd-chev" aria-hidden="true" />
                    </button>
                    {showQuiet && <ul className="hd-rows hd-quiet-list">{shownQuiet.map(renderRow)}</ul>}
                  </li>
                )}
              </ul>
              {!result.rows.length &&
                (filtering && raw?.rows.length ? (
                  <p className="hd-state">Nothing here for the selected companies.</p>
                ) : (
                  result.empty && <p className="hd-state">{result.empty}</p>
                ))}
              {filter && result.rows.length > 0 && !shownLoud.length && !shownQuiet.length && (
                <p className="hd-state">No rows match the filter.</p>
              )}
            </>
          )}
          <footer className="hd-section-foot">
            {active && selectedIndex >= 0 ? (
              <Hints row={selectedRow} />
            ) : (
              <span>{result?.foot ?? (state.updatedAt ? `Updated ${ago(new Date(state.updatedAt), now)}` : "")}</span>
            )}
            {def.link &&
              (isExternal(def.link.href) ? (
                <a href={def.link.href} target="_blank" rel="noopener noreferrer">
                  {def.link.label}
                </a>
              ) : (
                <Link to={def.link.href}>{def.link.label}</Link>
              ))}
          </footer>
        </>
      )}
    </section>
  )
}
